// lib
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  TextField,
} from "@material-ui/core";
import { Check, DeleteOutline, SwapVert, Edit, Undo } from "@material-ui/icons";
// src
import { PresetType, MY_PLANNING_ID } from "config/constants";
import { capitalizeFirstLetter } from "utils/string";
import Tooltip from "components/partials/Tooltip";
import Palette from "./Palette";

const renderIconButton = (label, handleClick, Icon) => (
  <IconButton
    className="not-handle"
    title={capitalizeFirstLetter(label)}
    size="small"
    edge="end"
    aria-label="options"
    onClick={handleClick}
  >
    <Icon />
  </IconButton>
);

const EditableLine = ({ preset, draggable, dispatch, onRemove }) => {
  const [newName, setNewName] = useState(preset.name);
  const inputNameRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (preset.editing) inputNameRef.current.focus();
  }, [preset.editing]);
  const handleChangeName = (e) => setNewName(e.target.value);

  const handleEdit = () => {
    dispatch({ type: "edit", index: preset.index });
  };
  const handleUnedit = () => {
    setNewName(preset.name);
    dispatch({ type: "unedit", index: preset.index });
  };
  const handleRename = () => dispatch({ type: "rename", index: preset.index, payload: newName });

  const handleRemove = () => {
    if (onRemove) {
      onRemove();
    } else {
      dispatch({ type: "remove", index: preset.index });
    }
  };
  const handleUpdateColor = (color) =>
    dispatch({ type: "update", index: preset.index, payload: { color } });

  const handleInputKeyDown = (e) => {
    if (e.keyCode === 13) {
      // enter
      handleRename();
    }
  };

  const renderActions = () => {
    if (preset.id === MY_PLANNING_ID) return null;
    return preset.editing ? (
      <>
        {renderIconButton(t("actions.cancel"), handleUnedit, Undo)}
        {renderIconButton(t("actions.confirm"), handleRename, Check)}
        {renderIconButton(t("actions.remove"), handleRemove, DeleteOutline)}
      </>
    ) : (
      <>
        {renderIconButton(t("actions.rename"), handleEdit, Edit)}
        {renderIconButton(t("actions.remove"), handleRemove, DeleteOutline)}
      </>
    );
  };

  const className = ((p) => {
    if (p.id === MY_PLANNING_ID) return "my-planning";
    if (p.type === PresetType.CATEGORY) return "border-category";
    if (p.underId) return "border-child";
    return "";
  })(preset);

  const renderListItemText = (p) => {
    if (p.type !== PresetType.CATEGORY) return <ListItemText>{p.name}</ListItemText>;
    const count = p.childrenCount
      ? t("favorites.planningCount", { count: p.childrenCount })
      : t("favorites.emptyCategory");

    return <ListItemText secondary={count}>{p.name}</ListItemText>;
  };

  return (
    <ListItem {...(draggable && draggable.dragHandleProps)}>
      <div className={className} />
      {preset.id !== MY_PLANNING_ID && (
        <ListItemIcon>
          <IconButton
            title={capitalizeFirstLetter(t("actions.move"))}
            size="small"
            edge="end"
            aria-label="options"
          >
            <SwapVert />
          </IconButton>
        </ListItemIcon>
      )}
      {preset.type !== PresetType.CATEGORY && (
        <ListItemIcon>
          <Tooltip
            AnchorType="button"
            main={
              <div
                role="button"
                aria-label="Color"
                className="fake-checkbox"
                style={{ backgroundColor: preset.color }}
              />
            }
            modifiers={{
              flip: {
                enabled: false,
              },
              preventOverflow: {
                enabled: true,
                boundariesElement: "scrollParent",
              },
            }}
            placement="bottom-start"
            className="palette-container"
            render={(props) => (
              <Palette preset={preset} updateColor={handleUpdateColor} {...props} />
            )}
          />
        </ListItemIcon>
      )}

      {preset.editing ? (
        <TextField
          inputRef={inputNameRef}
          value={newName}
          onChange={handleChangeName}
          inputProps={{ onKeyDown: handleInputKeyDown, maxlength: 40 }}
        />
      ) : (
        renderListItemText(preset)
      )}
      <ListItemSecondaryAction>{renderActions(draggable)}</ListItemSecondaryAction>
    </ListItem>
  );
};

EditableLine.propTypes = {
  // parent props
  preset: PropTypes.object.isRequired,
  draggable: PropTypes.object,
  dispatch: PropTypes.func,
  onRemove: PropTypes.func,
};

EditableLine.defaultProps = {
  draggable: null,
  dispatch: () => null,
  onRemove: null,
};

export default EditableLine;
